import {
  type RequestConfig,
  type ResponseConfig,
  axiosInstance,
  getConfig,
  setConfig,
} from './axiosClient';
import { branchControllerForkBranchWithoutPolicy } from './generated/kubb';

const shouldErrorGenerateFork = (error: unknown) => {
  const regex = /CONFLICTING_(ASSET|POLICY|CUSTOMER)_STATUS/;
  const branchRegex = /CONFLICTING_BRANCH_STATUS/;

  if (typeof error !== 'object' || error === null) {
    return false;
  }

  const errorResponse = (error as { errorResponse?: { status?: number; data?: { code?: string } } })
    .errorResponse;

  if (!errorResponse?.status || !errorResponse?.data?.code) {
    return false;
  }

  return (
    errorResponse.status === 409 &&
    (regex.test(errorResponse.data.code) || branchRegex.test(errorResponse.data.code))
  );
};
const clientWithFork = async <TData, _TError = unknown, TVariables = unknown>(
  requestConfig: RequestConfig<TVariables>,
): Promise<ResponseConfig<TData>> => {
  try {
    const mergedConfig = { ...getConfig(), ...requestConfig };

    const response = await axiosInstance.request(mergedConfig);

    return {
      data: { ...response.data, newBranchId: undefined },
      status: response.status,
      statusText: response.statusText,
      headers: response.headers,
    };
  } catch (error) {
    // Check if this error requires forking and we have the necessary parameters
    if (!shouldErrorGenerateFork(error)) {
      throw error;
    }

    let branchId: string | undefined;

    // Extract branchId from query params
    if (
      requestConfig.params &&
      typeof requestConfig.params === 'object' &&
      requestConfig.params !== null
    ) {
      branchId = (requestConfig.params as Record<string, unknown>).branchId as string;
    }

    if (!branchId) {
      throw error;
    }

    try {
      // Fork the branch
      const forkResponse = await branchControllerForkBranchWithoutPolicy(branchId, {
        closeOriginalBranch: false,
      });

      const newBranchId = forkResponse.id;

      // Retry the request with the new branch ID
      const newParams = {
        ...(requestConfig.params as Record<string, unknown>),
        branchId: newBranchId,
      };

      const mergedConfig = {
        ...getConfig(),
        ...requestConfig,
        params: newParams,
      };
      const response = await axiosInstance.request(mergedConfig);

      return {
        data: { ...response.data, newBranchId },
        status: response.status,
        statusText: response.statusText,
        headers: response.headers,
      };
    } catch {
      // If forking fails, throw the original error
      throw error;
    }
  }
};

// Attach configuration methods to clientWithFork
clientWithFork.getConfig = getConfig;
clientWithFork.setConfig = setConfig;

export { clientWithFork };
